// src/pages/caisse/DetailOperation.jsx
import React, { useState, useEffect } from "react";
import { 
    Card, Row, Col, Button, Spinner, Alert, 
    Modal, Tooltip, OverlayTrigger, Table
} from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import Footer from "../../layouts/Footer";
import Header from "../../layouts/Header";
import { formatCurrency, formatDate } from "../../utils/formatters";
import { API_BASE_URL } from "../../constants/api";
import logo from "../../assets/img/IMG-20250130-WA0008.jpg";
import { COLORS } from "../../constants/colors";

/* ============================================================
   TOAST NOTIFICATION — Design charte
   ============================================================ */
const ToastNotification = ({ show, onClose, type, title, message, autoHideDelay = 5000 }) => {
    if (!show) return null;
    const config = {
        success: { bgColor: COLORS.success || '#10b981', icon: 'ri-checkbox-circle-fill' },
        error:   { bgColor: COLORS.danger  || '#ef4444', icon: 'ri-error-warning-fill' },
        info:    { bgColor: COLORS.info    || '#3b82f6', icon: 'ri-information-fill' },
        warning: { bgColor: COLORS.warning || '#f59e0b', icon: 'ri-alert-fill' }
    }[type] || { bgColor: COLORS.info || '#3b82f6', icon: 'ri-information-fill' };

    return (
        <div className="toast-notification-fixed">
            <div className="toast-modern">
                <div className="toast-header-custom" style={{ background: config.bgColor }}>
                    <div className="d-flex align-items-center gap-2">
                        <i className={`${config.icon} text-white`} style={{ fontSize: '1.5rem' }}></i>
                        <strong className="text-white fs-6">{title}</strong>
                    </div>
                    <button onClick={onClose} className="toast-close-btn">
                        <i className="ri-close-line fs-5"></i>
                    </button>
                </div>
                <div className="toast-body-custom"><p className="mb-0">{message}</p></div>
                <div className="toast-progress" style={{ background: config.bgColor, animation: `shrink ${autoHideDelay}ms linear forwards` }}></div>
            </div>
        </div>
    );
};

/* ============================================================
   COMPOSANT PRINCIPAL
   ============================================================ */
export default function DetailOperation() {
    const navigate = useNavigate();
    const { id } = useParams();

    const [operation, setOperation] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [societeInfos, setSocieteInfos] = useState(null);
    const [logoBase64, setLogoBase64] = useState('');
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [toast, setToast] = useState({ show: false, type: '', title: '', message: '' });
 const company = {
        name: societeInfos?.nom || 'CMS',
        address: societeInfos?.adresse || 'Libreville, Gabon',
        phone: societeInfos?.telephone || '+241 00 00 00 00',
        email: societeInfos?.email || 'contact@geotech.com',
        site_web: societeInfos?.site_web || 'www.cms-gabon.com'
    };
    const showToast = (type, title, message) => setToast({ show: true, type, title, message });
    const hideToast = () => setToast({ show: false, type: '', title: '', message: '' });

    useEffect(() => {
        fetchOperation();
        fetchSocieteInfos();
        loadLogoBase64();
    }, [id]);

    /* ============================================================
       FETCH
       ============================================================ */
    const fetchSocieteInfos = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/parametres.php?action=societe`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (response.ok) {
                const result = await response.json();
                if (result.data) setSocieteInfos(result.data);
            }
        } catch (err) {
            console.error("Erreur chargement société", err);
        }
    };

    const loadLogoBase64 = () => {
        return new Promise((resolve) => {
            try {
                const img = new Image();
                img.crossOrigin = "Anonymous";
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    canvas.width = img.width;
                    canvas.height = img.height;
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0);
                    setLogoBase64(canvas.toDataURL('image/jpeg'));
                    resolve();
                };
                img.onerror = () => { setLogoBase64(''); resolve(); };
                img.src = logo;
            } catch (error) {
                setLogoBase64('');
                resolve();
            }
        });
    };

    const fetchOperation = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_BASE_URL}/caisse.php?id=${id}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!response.ok) throw new Error("Erreur chargement");
            const result = await response.json();
            setOperation(result.data);
        } catch (err) {
            setError(err.message);
            showToast('error', 'Erreur', err.message);
        } finally {
            setLoading(false);
        }
    };

    /* ============================================================
       ACTIONS
       ============================================================ */
    const handleDelete = async () => {
        setDeleting(true);
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_BASE_URL}/caisse.php?id=${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const result = await response.json();
            if (result.success) {
                setShowDeleteModal(false);
                showToast('success', 'Succès', "Opération supprimée avec succès");
                setTimeout(() => navigate('/caisse/operations'), 1500);
            } else {
                showToast('error', 'Erreur', result.message || "Erreur lors de la suppression");
                setShowDeleteModal(false);
            }
        } catch (err) {
            showToast('error', 'Erreur', "Erreur lors de la suppression");
            setShowDeleteModal(false);
        } finally {
            setDeleting(false);
        }
    };

    const handleAddPaiement = () => {
        const montantTotal = Number(operation.montant_initial) || Number(operation.montant) || 0;
        const avanceInitiale = Number(operation.avance) || 0;
        let sommePaiements = 0;
        const paiementsList = operation.paiements || [];
        for (let i = 0; i < paiementsList.length; i++) sommePaiements += Number(paiementsList[i].avance) || 0;
        const totalVersé = avanceInitiale + sommePaiements;
        const resteReel = Math.max(0, montantTotal - totalVersé);
        const pourcentagePaye = montantTotal > 0 ? (totalVersé / montantTotal) * 100 : 0;

        if (resteReel <= 0) {
            showToast('warning', 'Attention', "Ce décaissement est déjà soldé");
            return;
        }

        navigate('/caisse/decaissement', {
            state: {
                parent_id: operation.id,
                beneficiaire_nom: operation.beneficiaire_nom,
                beneficiaire_prenom: operation.beneficiaire_prenom,
                motif: operation.motif,
                montant_initial: montantTotal,
                avance_actuelle: totalVersé,
                reste: resteReel,
                pourcentage_paye: pourcentagePaye,
                numero_bordereau_parent: operation.numero_bordereau,
                date_signature: operation.date_signature,
                fait_a: operation.fait_a
            }
        });
    };

    /* ============================================================
       IMPRESSION (logique conservée)
       ============================================================ */
    const genererHTMLImpressionPaiement = (paiement, montantDuAvant, montantPaye, nouveauReste, montantTotal, avanceInitiale) => {
        const logoSrc = logoBase64;
        const company = {
            name: societeInfos?.nom || 'GEOTECH',
            address: societeInfos?.adresse || 'Libreville, Gabon',
            phone: societeInfos?.telephone || '+241 00 00 00 00',
            email: societeInfos?.email || 'contact@geotech.com'
        };
        return `<!DOCTYPE html><html lang="fr"><head><meta charset="UTF-8"><title>Bordereau de paiement ${paiement.numero_bordereau}</title><style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: Arial, sans-serif; background: white; color: #000; font-size: 11pt; }
            .bordereau-container { width: 100%; max-width: 210mm; margin: 0 auto; }
            .header { padding: 8px 15px; border-bottom: 1px solid #000; display: flex; justify-content: space-between; align-items: center; }
            .logo-img { height: 45px; width: auto; }
            .company-name { font-size: 13pt; font-weight: bold; }
            .document-title { font-size: 15pt; font-weight: bold; text-align: center; }
            .document-number { font-size: 10pt; text-align: center; }
            .date { font-size: 10pt; text-align: right; }
            .contact-bar { padding: 8px 15px; background: #f8f8f8; border-bottom: 1px solid #e0e0e0; display: flex; justify-content: space-between; font-size: 9pt; flex-wrap: wrap; gap: 10px; }
            .body { padding: 12px 15px; }
            .section-title { font-size: 11pt; font-weight: bold; text-transform: uppercase; border-bottom: 1px solid #000; padding-bottom: 2px; margin-bottom: 8px; }
            .beneficiary-box { border: 1px solid #ccc; padding: 8px; }
            .beneficiary-name { font-size: 12pt; font-weight: bold; margin-bottom: 5px; }
            .amounts-table { width: 100%; border-collapse: collapse; margin-bottom: 15px; }
            .amounts-table td { border: 1px solid #000; padding: 6px; font-size: 10pt; }
            .amounts-table .label { font-weight: bold; background: #f9f9f9; width: 25%; }
            .amounts-table .value { font-weight: bold; text-align: right; font-size: 11pt; }
            .signature-box { margin-top: 20px; display: flex; justify-content: space-between; }
            .signature-item { width: 45%; text-align: center; }
            .signature-line { border-top: 1px solid #000; margin: 20px 0 3px 0; }
            .signature-label { font-size: 9pt; font-style: italic; }
            .signature-img { max-width: 130px; max-height: 45px; margin-bottom: 5px; }
            .footer { padding: 6px 15px; border-top: 1px solid #ddd; background: #f8f8f8; font-size: 8pt; text-align: center; }
        </style></head><body>
            <div class="bordereau-container">
                <div class="header">
                    <div>${logoSrc ? `<img src="${logoSrc}" class="logo-img" />` : `<div class="company-name">${company.name}</div>`}</div>
                    <div><div class="document-title">BORDEREAU DE PAIEMENT</div><div class="document-number">N° ${paiement.numero_bordereau}</div></div>
                    <div class="date">${new Date().toLocaleDateString('fr-FR')}</div>
                </div>
                <div class="contact-bar">
                    <span>${company.address}</span><span>${company.phone}</span><span>${company.email}</span>
                </div>
                <div class="body">
                    <div style="margin-bottom:15px;">
                        <div class="section-title">BÉNÉFICIAIRE</div>
                        <div class="beneficiary-box">
                            <div class="beneficiary-name">${paiement.beneficiaire_prenom} ${paiement.beneficiaire_nom.toUpperCase()}</div>
                            <div style="font-size:10pt;padding-top:5px;border-top:1px dashed #ccc;"><strong>Motif :</strong> ${paiement.motif}</div>
                        </div>
                    </div>
                    <div style="margin-bottom:15px;">
                        <div class="section-title">DÉTAIL DU PAIEMENT</div>
                        <table class="amounts-table">
                            <tr><td class="label">Montant dû avant</td><td class="value">${formatCurrency(montantDuAvant)}</td><td class="label">Montant payé</td><td class="value">${formatCurrency(montantPaye)}</td></tr>
                            <tr><td class="label" colspan="2">Reste après paiement</td><td class="value" colspan="2">${formatCurrency(nouveauReste)}</td></tr>
                        </table>
                    </div>
                    <div class="signature-box">
                        <div class="signature-item">${paiement.signature ? `<img src="${paiement.signature}" class="signature-img" />` : '<div class="signature-line"></div>'}<div class="signature-label">Signature du bénéficiaire</div></div>
                        <div class="signature-item">${paiement.cachet ? `<img src="${paiement.cachet}" class="signature-img" />` : '<div class="signature-line"></div>'}<div class="signature-label">Cachet de l'entreprise</div></div>
                    </div>
                </div>
                <div class="footer">Document généré le ${new Date().toLocaleDateString('fr-FR')}</div>
            </div>
        </body></html>`;
    };

    const genererHTMLImpression = () => {
        if (!operation) return '';
        const company = {
            name: societeInfos?.nom || 'GEOTECH',
            address: societeInfos?.adresse || 'Libreville, Gabon',
            phone: societeInfos?.telephone || '+241 00 00 00 00',
            email: societeInfos?.email || 'contact@geotech.com'
        };
        const montantTotal = Number(operation.montant_initial) || Number(operation.montant) || 0;
        const avanceInitiale = Number(operation.avance) || 0;
        const paiementsList = operation.paiements || [];
        let sommePaiements = 0;
        for (let i = 0; i < paiementsList.length; i++) sommePaiements += Number(paiementsList[i].avance) || 0;
        const totalVersé = avanceInitiale + sommePaiements;
        const resteGlobal = montantTotal - totalVersé;
        const logoSrc = logoBase64;

        let historiqueRows = '';
        const resteApresInitial = montantTotal - avanceInitiale;
        historiqueRows += `<tr><td style="text-align:center;"><strong>Initial</strong></td><td><code>${operation.numero_bordereau}</code></td><td>${formatDate(operation.created_at)}<br/><small>${new Date(operation.created_at).toLocaleTimeString('fr-FR')}</small></td><td style="text-align:right;"><strong>${formatCurrency(montantTotal)}</strong></td><td style="text-align:right;"><strong>${formatCurrency(avanceInitiale)}</strong></td><td style="text-align:right;"><strong>${formatCurrency(resteApresInitial)}</strong></td></tr>`;

        if (paiementsList.length > 0) {
            let montantDuAvant = resteApresInitial;
            for (let i = 0; i < paiementsList.length; i++) {
                const p = paiementsList[i];
                const montantPaye = Number(p.avance) || 0;
                const nouveauReste = montantDuAvant - montantPaye;
                historiqueRows += `<tr><td style="text-align:center;">Paiement ${i + 1}</td><td><code>${p.numero_bordereau}</code></td><td>${formatDate(p.created_at)}<br/><small>${new Date(p.created_at).toLocaleTimeString('fr-FR')}</small></td><td style="text-align:right;">${formatCurrency(montantDuAvant)}</td><td style="text-align:right;">${formatCurrency(montantPaye)}</td><td style="text-align:right;">${formatCurrency(nouveauReste)}</td></tr>`;
                montantDuAvant = nouveauReste;
            }
        }

        return `<!DOCTYPE html><html lang="fr"><head><meta charset="UTF-8"><title>Bordereau ${operation.numero_bordereau}</title><style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: Arial, sans-serif; background: white; color: #000; font-size: 11pt; }
            .bordereau-container { width: 100%; max-width: 210mm; margin: 0 auto; }
            .header { padding: 8px 15px; border-bottom: 1px solid #000; display: flex; justify-content: space-between; align-items: center; }
            .logo-img { height: 45px; }
            .document-title { font-size: 15pt; font-weight: bold; text-align: center; }
            .document-number { font-size: 10pt; text-align: center; }
            .contact-bar { padding: 8px 15px; background: #f8f8f8; border-bottom: 1px solid #e0e0e0; display: flex; justify-content: space-between; font-size: 9pt; }
            .body { padding: 12px 15px; }
            .section-title { font-size: 11pt; font-weight: bold; text-transform: uppercase; border-bottom: 1px solid #000; padding-bottom: 2px; margin-bottom: 8px; }
            .amounts-table { width: 100%; border-collapse: collapse; margin-bottom: 15px; }
            .amounts-table td { border: 1px solid #000; padding: 6px; font-size: 10pt; }
            .amounts-table .label { font-weight: bold; background: #f9f9f9; width: 25%; }
            .amounts-table .value { font-weight: bold; text-align: right; font-size: 11pt; }
            .historique-table { width: 100%; border-collapse: collapse; margin-top: 10px; margin-bottom: 15px; }
            .historique-table th, .historique-table td { border: 1px solid #000; padding: 6px; font-size: 9pt; }
            .historique-table th { background: #f0f0f0; font-weight: bold; text-align: center; }
            .signature-box { margin-top: 20px; display: flex; justify-content: space-between; }
            .signature-item { width: 45%; text-align: center; }
            .signature-line { border-top: 1px solid #000; margin: 20px 0 3px 0; }
            .signature-label { font-size: 9pt; font-style: italic; }
            .signature-img { max-width: 130px; max-height: 45px; margin-bottom: 5px; }
            .footer { padding: 6px 15px; border-top: 1px solid #ddd; background: #f8f8f8; font-size: 8pt; text-align: center; }
        </style></head><body>
            <div class="bordereau-container">
                <div class="header">
                    <div>${logoSrc ? `<img src="${logoSrc}" class="logo-img" />` : `<div style="font-size:13pt;font-weight:bold;">${company.name}</div>`}</div>
                    <div><div class="document-title">BORDEREAU DE DÉCAISSEMENT</div><div class="document-number">N° ${operation.numero_bordereau}</div></div>
                    <div style="font-size:10pt;">${new Date().toLocaleDateString('fr-FR')}</div>
                </div>
                <div class="contact-bar"><span>${company.address}</span><span>${company.phone}</span><span>${company.email}</span></div>
                <div class="body">
                    <div style="margin-bottom:15px;">
                        <div class="section-title">BÉNÉFICIAIRE</div>
                        <div style="border:1px solid #ccc;padding:8px;">
                            <div style="font-size:12pt;font-weight:bold;margin-bottom:5px;">${operation.beneficiaire_prenom} ${operation.beneficiaire_nom.toUpperCase()}</div>
                            <div style="font-size:10pt;padding-top:5px;border-top:1px dashed #ccc;"><strong>Motif :</strong> ${operation.motif}</div>
                        </div>
                    </div>
                    <div style="margin-bottom:15px;">
                        <div class="section-title">MONTANTS</div>
                        <table class="amounts-table">
                            <tr><td class="label">Montant total</td><td class="value">${formatCurrency(montantTotal)}</td><td class="label">Total versé</td><td class="value">${formatCurrency(totalVersé)}</td></tr>
                            <tr><td class="label" colspan="2">Reste à payer</td><td class="value" colspan="2">${formatCurrency(resteGlobal)}</td></tr>
                        </table>
                    </div>
                    <div style="margin-bottom:15px;">
                        <div class="section-title">HISTORIQUE DES TRANSACTIONS</div>
                        <table class="historique-table">
                            <thead><tr><th>Type</th><th>Bordereau</th><th>Date</th><th style="text-align:right;">Montant dû</th><th style="text-align:right;">Montant payé</th><th style="text-align:right;">Reste</th></tr></thead>
                            <tbody>${historiqueRows}</tbody>
                            <tfoot><tr style="background:#f0f0f0;font-weight:bold;"><td colspan="3" style="text-align:right;">Récapitulatif :</td><td style="text-align:right;">${formatCurrency(montantTotal)}</td><td style="text-align:right;">${formatCurrency(totalVersé)}</td><td style="text-align:right;">${formatCurrency(resteGlobal)}</td></tr></tfoot>
                        </table>
                    </div>
                    <div class="signature-box">
                        <div class="signature-item">${operation.signature ? `<img src="${operation.signature}" class="signature-img" />` : '<div class="signature-line"></div>'}<div class="signature-label">Signature du bénéficiaire</div></div>
                        <div class="signature-item">${operation.cachet ? `<img src="${operation.cachet}" class="signature-img" />` : '<div class="signature-line"></div>'}<div class="signature-label">Cachet de l'entreprise</div></div>
                    </div>
                </div>
                <div class="footer">Document généré le ${new Date().toLocaleDateString('fr-FR')}</div>
            </div>
        </body></html>`;
    };

    const handlePrintPaiement = (htmlContent) => {
        const iframe = document.createElement('iframe');
        iframe.style.position = 'absolute';
        iframe.style.width = '0';
        iframe.style.height = '0';
        iframe.style.border = 'none';
        document.body.appendChild(iframe);
        const iframeDoc = iframe.contentWindow.document;
        iframeDoc.open();
        iframeDoc.write(htmlContent);
        iframeDoc.close();
        iframe.contentWindow.onload = () => {
            iframe.contentWindow.print();
            setTimeout(() => {
                if (document.body.contains(iframe)) document.body.removeChild(iframe);
            }, 1000);
        };
    };

    const handlePrint = () => {
        const htmlContent = genererHTMLImpression();
        handlePrintPaiement(htmlContent);
        showToast('info', 'Impression', "Document envoyé à l'impression");
    };

    const ActionButton = ({ icon, onClick, tooltip, color }) => (
        <OverlayTrigger placement="top" overlay={<Tooltip>{tooltip}</Tooltip>}>
            <button
                className="action-btn-charte"
                onClick={onClick}
                style={{ '--btn-color': color || COLORS.primary }}
            >
                <i className={icon}></i>
            </button>
        </OverlayTrigger>
    );

    /* ============================================================
       LOADING / ERROR
       ============================================================ */
    if (loading && !operation) {
      
             return (
                 <>
                     <Header />
                     <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                         <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 100px)' }}>
                             <div className="text-center" style={{ maxWidth: '400px' }}>
                                 <div className="loading-ring-wrapper mb-4">
                                     <div className="loading-ring"></div>
                                     <div className="loading-ring-inner"></div>
                                     <i className="ri-loader-4-fill loading-icon"></i>
                                 </div>
                                 <h5 className="fw-bold" style={{ color: COLORS.primaryDark, letterSpacing: '0.3px' }}>
                                     Chargement de la page...
                                 </h5>
                                 <p className="text-muted small mb-0">Veuillez patienter quelques instants...</p>
                                 <div className="mx-auto mt-4" style={{ width: '80%' }}>
                                     <div className="progress" style={{ height: '3px', background: '#e9ecef', borderRadius: '10px', overflow: 'hidden' }}>
                                         <div
                                             className="progress-bar progress-bar-striped progress-bar-animated"
                                             style={{
                                                 width: '100%',
                                                 background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary}, ${COLORS.primary})`,
                                                 backgroundSize: '200% 100%',
                                                 animation: 'shimmer 1.5s linear infinite'
                                             }}
                                         />
                                     </div>
                                 </div>
                             </div>
                         </div>
                     </div>
                     <Footer />
                     <style>{`
                         .loading-ring-wrapper { position: relative; width: 80px; height: 80px; margin: 0 auto; }
                         .loading-ring { position: absolute; top: 0; left: 0; width: 100%; height: 100%; border-radius: 50%; border: 4px solid rgba(33,172,205,0.15); border-top-color: ${COLORS.primary}; animation: spin 1s linear infinite; }
                         .loading-ring-inner { position: absolute; top: 8px; left: 8px; right: 8px; bottom: 8px; border-radius: 50%; border: 3px solid rgba(33,172,205,0.08); border-bottom-color: ${COLORS.secondary}; animation: spin 0.8s linear infinite reverse; }
                         .loading-icon { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); font-size: 28px; color: ${COLORS.primary}; animation: pulse-icon 1.2s ease-in-out infinite; }
                         @keyframes spin { to { transform: rotate(360deg); } }
                         @keyframes pulse-icon { 0%,100% { transform: translate(-50%,-50%) scale(1); opacity:1; } 50% { transform: translate(-50%,-50%) scale(1.15); opacity:0.6; } }
                         @keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
                     `}</style>
                 </>
             );
         }

    if (error || !operation) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <Alert className="alert-error-charte">
                        <div className="d-flex align-items-center">
                            <div className="alert-error-icon"><i className="ri-error-warning-line"></i></div>
                            <div className="ms-3 flex-grow-1">
                                <h6 className="mb-1 fw-bold">Erreur de chargement</h6>
                                <p className="mb-0 small">{error || "Opération non trouvée"}</p>
                            </div>
                            <Button onClick={() => navigate(-1)} className="btn-modern-outline">
                                <i className="ri-arrow-left-line me-1"></i>Retour
                            </Button>
                        </div>
                    </Alert>
                </div>
                <Footer />
            </>
        );
    }

    /* ============================================================
       CALCULS
       ============================================================ */
    const montantTotal = Number(operation.montant_initial) || Number(operation.montant) || 0;
    const avanceInitiale = Number(operation.avance) || 0;
    let sommePaiements = 0;
    const paiementsList = operation.paiements || [];
    for (let i = 0; i < paiementsList.length; i++) sommePaiements += Number(paiementsList[i].avance) || 0;
    const totalVersé = avanceInitiale + sommePaiements;
    const resteGlobal = montantTotal - totalVersé;
    const pourcentagePaye = montantTotal > 0 ? (totalVersé / montantTotal) * 100 : 0;

    const getStatutConfig = () => {
        if (resteGlobal <= 0) return { gradient: 'linear-gradient(135deg, #10b981, #059669)', label: 'Soldé', icon: 'ri-checkbox-circle-line' };
        if (totalVersé > 0) return { gradient: 'linear-gradient(135deg, #f59e0b, #d97706)', label: 'Avance partielle', icon: 'ri-time-line' };
        return { gradient: 'linear-gradient(135deg, #ef4444, #dc2626)', label: 'Impayé', icon: 'ri-error-warning-line' };
    };
    const statut = getStatutConfig();
    const pourcentageAffichage = pourcentagePaye % 1 === 0 ? pourcentagePaye : pourcentagePaye.toFixed(1);

    /* ============================================================
       RENDU PRINCIPAL
       ============================================================ */
    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>

                <ToastNotification
                    show={toast.show}
                    onClose={hideToast}
                    type={toast.type}
                    title={toast.title}
                    message={toast.message}
                />

                <div className="container-fluid">

                    {/* ===== EN-TÊTE MODERNE ===== */}
                    <div className="header-modern mb-4">
                        <div className="header-left">
                            <div className="header-icon-wrapper">
                                <i className="ri-bank-card-line"></i>
                            </div>
                            <div>
                                <h5 className="header-title">Détail du décaissement</h5>
                                <div className="d-flex flex-wrap gap-2 mt-1">
                                    <span className="badge-modern">
                                        <i className="ri-barcode-line"></i>
                                        {operation.numero_bordereau}
                                    </span>
                                    <span className="status-badge-charte" style={{ background: statut.gradient }}>
                                        <i className={statut.icon}></i>
                                        {statut.label}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 flex-wrap">
                            <ActionButton icon="ri-arrow-left-line" onClick={() => navigate('/caisse/operations')} tooltip="Retour" />
                            <ActionButton icon="ri-printer-line" onClick={handlePrint} tooltip="Imprimer" color={COLORS.secondary} />
                            <ActionButton icon="ri-delete-bin-line" onClick={() => setShowDeleteModal(true)} tooltip="Supprimer" color={COLORS.danger} />
                            <Button
                                onClick={handleAddPaiement}
                                className="btn-modern-primary"
                                disabled={resteGlobal <= 0}
                            >
                                <i className="ri-add-line me-2"></i>Ajouter un paiement
                            </Button>
                        </div>
                    </div>

                    {/* ===== CARTE RÉSUMÉ ===== */}
                    <Card className="border-0 shadow-sm rounded-3 mb-4">
                        <Card.Body className="p-4">
                            <Row className="align-items-center g-3">
                                <Col md={4}>
                                    <div className="stat-item-charte">
                                        <div className="stat-icon-charte" style={{ background: `${COLORS.primary}15`, color: COLORS.primary }}>
                                            <i className="ri-barcode-line"></i>
                                        </div>
                                        <div>
                                            <span className="stat-label-charte">Bordereau</span>
                                            <div className="stat-value-charte" style={{ color: COLORS.primary }}>{operation.numero_bordereau}</div>
                                        </div>
                                    </div>
                                </Col>
                                <Col md={4}>
                                    <div className="stat-item-charte">
                                        <div className="stat-icon-charte" style={{ background: `${COLORS.success}15`, color: COLORS.success }}>
                                            <i className="ri-calendar-line"></i>
                                        </div>
                                        <div>
                                            <span className="stat-label-charte">Date création</span>
                                            <div className="stat-value-charte">{formatDate(operation.created_at)}</div>
                                            <span className="kpi-unit-charte">{new Date(operation.created_at).toLocaleTimeString('fr-FR')}</span>
                                        </div>
                                    </div>
                                </Col>
                                <Col md={4}>
                                    <div className="stat-item-charte">
                                        <div className="stat-icon-charte" style={{ background: `${COLORS.secondary}15`, color: COLORS.secondary }}>
                                            <i className="ri-money-euro-circle-line"></i>
                                        </div>
                                        <div>
                                            <span className="stat-label-charte">Montant total</span>
                                            <div className="stat-value-charte" style={{ color: COLORS.primary, fontWeight: 700 }}>
                                                {formatCurrency(montantTotal)}
                                            </div>
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* ===== INFOS SOCIÉTÉ & BÉNÉFICIAIRE ===== */}
                    <Row className="g-3 mb-4">
                        <Col md={6}>
                            <Card className="border-0 shadow-sm rounded-3 h-100 card-hover-charte">
                                <Card.Header className="card-section-header">
                                    <div className="section-icon-charte" style={{ background: `${COLORS.primary}15`, color: COLORS.primary }}>
                                        <i className="ri-building-line"></i>
                                    </div>
                                    <h6 className="mb-0 fw-semibold">Informations société</h6>
                                </Card.Header>
                                <Card.Body>
                                    <h5 className="fw-bold mb-3" style={{ color: COLORS.primary }}>{company.name}</h5>
                                    <p className="info-line-charte"><i className="ri-map-pin-line"></i>{company.address}</p>
                                    <p className="info-line-charte"><i className="ri-phone-line"></i>{company.phone}</p>
                                    <p className="info-line-charte"><i className="ri-mail-line"></i>{company.email}</p>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={6}>
                            <Card className="border-0 shadow-sm rounded-3 h-100 card-hover-charte">
                                <Card.Header className="card-section-header">
                                    <div className="section-icon-charte" style={{ background: `${COLORS.secondary}15`, color: COLORS.secondary }}>
                                        <i className="ri-user-line"></i>
                                    </div>
                                    <h6 className="mb-0 fw-semibold">Informations bénéficiaire</h6>
                                </Card.Header>
                                <Card.Body>
                                    <h5 className="fw-bold mb-3" style={{ color: COLORS.secondary }}>
                                        {operation.beneficiaire_prenom} {operation.beneficiaire_nom}
                                    </h5>
                                    <p className="info-line-charte"><i className="ri-question-line"></i>Motif: {operation.motif}</p>
                                    {operation.fait_a && <p className="info-line-charte"><i className="ri-map-pin-line"></i>Lieu: {operation.fait_a}</p>}
                                    {operation.date_signature && (
                                        <p className="info-line-charte"><i className="ri-calendar-line"></i>Date signature: {formatDate(operation.date_signature)}</p>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    {/* ===== CARTES DES MONTANTS ===== */}
                    <Row className="g-3 mb-4">
                        <Col md={4}>
                            <div className="stat-card-charte kpi-primary">
                                <div className="stat-icon-charte" style={{ background: `${COLORS.primary}15`, color: COLORS.primary }}>
                                    <i className="ri-money-euro-circle-line"></i>
                                </div>
                                <small className="stat-label-charte">Montant total</small>
                                <h3 className="stat-value-charte" style={{ color: COLORS.primary }}>{formatCurrency(montantTotal)}</h3>
                            </div>
                        </Col>
                        <Col md={4}>
                            <div className="stat-card-charte kpi-success">
                                <div className="stat-icon-charte" style={{ background: `${COLORS.success}15`, color: COLORS.success }}>
                                    <i className="ri-hand-coin-line"></i>
                                </div>
                                <small className="stat-label-charte">Total versé</small>
                                <h3 className="stat-value-charte" style={{ color: COLORS.success }}>{formatCurrency(totalVersé)}</h3>
                            </div>
                        </Col>
                        <Col md={4}>
                            <div className="stat-card-charte" style={{ borderLeftColor: resteGlobal > 0 ? COLORS.danger : COLORS.success }}>
                                <div className="stat-icon-charte" style={{
                                    background: resteGlobal > 0 ? `${COLORS.danger}15` : `${COLORS.success}15`,
                                    color: resteGlobal > 0 ? COLORS.danger : COLORS.success
                                }}>
                                    <i className="ri-hourglass-line"></i>
                                </div>
                                <small className="stat-label-charte">Reste à payer</small>
                                <h3 className="stat-value-charte" style={{ color: resteGlobal > 0 ? COLORS.danger : COLORS.success }}>
                                    {formatCurrency(resteGlobal)}
                                </h3>
                            </div>
                        </Col>
                    </Row>

                    {/* ===== BARRE DE PROGRESSION ===== */}
                    <Card className="border-0 shadow-sm rounded-3 mb-4">
                        <Card.Body className="p-4">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <span className="fw-semibold" style={{ color: COLORS.primaryDark }}>
                                    <i className="ri-pie-chart-line me-2"></i>Progression du paiement
                                </span>
                                <span className="fw-bold" style={{ color: COLORS.success, fontSize: '1.1rem' }}>
                                    {pourcentageAffichage}%
                                </span>
                            </div>
                            <div className="progress-charte-large">
                                <div className="progress-bar-charte-large" style={{ width: `${pourcentagePaye}%` }}>
                                    {pourcentagePaye > 10 && `${pourcentageAffichage}%`}
                                </div>
                            </div>
                        </Card.Body>
                    </Card>

                    {/* ===== HISTORIQUE ===== */}
                    {paiementsList.length > 0 && (
                        <Card className="border-0 shadow-sm rounded-3 mb-4">
                            <Card.Header className="card-header-gradient">
                                <div className="d-flex align-items-center gap-2">
                                    <i className="ri-history-line text-white fs-5"></i>
                                    <span className="fw-semibold text-white">Historique des transactions</span>
                                    <span className="badge-header-light">{paiementsList.length + 1}</span>
                                </div>
                            </Card.Header>
                            <Card.Body className="p-0">
                                <div className="table-responsive">
                                    <Table hover className="align-middle mb-0 table-charte">
                                        <thead>
                                            <tr>
                                                <th className="ps-4">Type</th>
                                                <th>Bordereau</th>
                                                <th>Date</th>
                                                <th className="text-end">Montant dû</th>
                                                <th className="text-end">Avance payée</th>
                                                <th className="text-end">Reste</th>
                                                <th className="text-center">Signature</th>
                                                <th className="text-center pe-4">Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {/* Transaction initiale */}
                                            <tr className="row-initial-charte">
                                                <td className="ps-4">
                                                    <span className="badge-type-charte" style={{ background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryDark})` }}>
                                                        <i className="ri-add-line"></i>Initial
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className="reference-badge-charte">
                                                        <i className="ri-barcode-line"></i>{operation.numero_bordereau}
                                                    </span>
                                                </td>
                                                <td>
                                                    <div className="small">{formatDate(operation.created_at)}</div>
                                                    <small className="text-muted">{new Date(operation.created_at).toLocaleTimeString('fr-FR')}</small>
                                                </td>
                                                <td className="text-end fw-bold">{formatCurrency(montantTotal)}</td>
                                                <td className="text-end fw-bold" style={{ color: COLORS.warning }}>
                                                    {formatCurrency(avanceInitiale)}
                                                </td>
                                                <td className="text-end fw-bold" style={{ color: COLORS.danger }}>
                                                    {formatCurrency(montantTotal - avanceInitiale)}
                                                </td>
                                                <td className="text-center">
                                                    {operation.signature
                                                        ? <i className="ri-checkbox-circle-fill" style={{ color: COLORS.success, fontSize: '1.1rem' }} title="Signé"></i>
                                                        : <i className="ri-close-circle-line text-muted" title="Non signé"></i>}
                                                </td>
                                                <td className="text-center pe-4"></td>
                                            </tr>

                                            {/* Paiements */}
                                            {(() => {
                                                const paiementRows = [];
                                                const sortedPaiements = [...paiementsList].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
                                                let resteAvant = montantTotal - avanceInitiale;
                                                for (let idx = 0; idx < sortedPaiements.length; idx++) {
                                                    const paiement = sortedPaiements[idx];
                                                    const montantPaye = Number(paiement.avance) || 0;
                                                    const nouveauReste = Math.max(0, resteAvant - montantPaye);
                                                    const montantDuAvantIteration = resteAvant;
                                                    paiementRows.push(
                                                        <tr key={paiement.id || idx}>
                                                            <td className="ps-4">
                                                                <span className="badge-type-charte" style={{ background: `linear-gradient(135deg, ${COLORS.secondary}, #0d8ba6)` }}>
                                                                    <i className="ri-bank-card-line"></i>Paiement #{idx + 1}
                                                                </span>
                                                            </td>
                                                            <td>
                                                                <span className="reference-badge-charte">
                                                                    <i className="ri-barcode-line"></i>{paiement.numero_bordereau}
                                                                </span>
                                                            </td>
                                                            <td>
                                                                <div className="small">{formatDate(paiement.created_at)}</div>
                                                                <small className="text-muted">{new Date(paiement.created_at).toLocaleTimeString('fr-FR')}</small>
                                                            </td>
                                                            <td className="text-end fw-bold">{formatCurrency(montantDuAvantIteration)}</td>
                                                            <td className="text-end fw-bold" style={{ color: COLORS.success }}>
                                                                {formatCurrency(montantPaye)}
                                                            </td>
                                                            <td className="text-end">
                                                                {nouveauReste <= 0
                                                                    ? <span className="badge-solde-charte"><i className="ri-check-line"></i>Soldé</span>
                                                                    : <span className="badge-reste-charte">{formatCurrency(nouveauReste)}</span>}
                                                            </td>
                                                            <td className="text-center">
                                                                {paiement.signature
                                                                    ? <i className="ri-checkbox-circle-fill" style={{ color: COLORS.success, fontSize: '1.1rem' }} title="Signé"></i>
                                                                    : <i className="ri-close-circle-line text-muted" title="Non signé"></i>}
                                                            </td>
                                                            <td className="text-center pe-4">
                                                                <OverlayTrigger placement="top" overlay={<Tooltip>Imprimer ce paiement</Tooltip>}>
                                                                    <button
                                                                        className="action-btn-charte"
                                                                        style={{ '--btn-color': COLORS.secondary }}
                                                                        onClick={() => {
                                                                            const html = genererHTMLImpressionPaiement(
                                                                                paiement, montantDuAvantIteration, montantPaye,
                                                                                nouveauReste, montantTotal, avanceInitiale
                                                                            );
                                                                            handlePrintPaiement(html);
                                                                        }}
                                                                    >
                                                                        <i className="ri-printer-line"></i>
                                                                    </button>
                                                                </OverlayTrigger>
                                                            </td>
                                                        </tr>
                                                    );
                                                    resteAvant = nouveauReste;
                                                }
                                                return paiementRows;
                                            })()}
                                        </tbody>
                                        <tfoot>
                                            <tr>
                                                <td colSpan="3" className="ps-4 fw-semibold">Récapitulatif</td>
                                                <td className="text-end fw-bold">{formatCurrency(montantTotal)}</td>
                                                <td className="text-end fw-bold" style={{ color: COLORS.success }}>{formatCurrency(totalVersé)}</td>
                                                <td className="text-end fw-bold" style={{ color: COLORS.danger }}>{formatCurrency(resteGlobal)}</td>
                                                <td colSpan="2"></td>
                                            </tr>
                                        </tfoot>
                                    </Table>
                                </div>
                            </Card.Body>
                        </Card>
                    )}

                    {/* ===== SIGNATURE & CACHET ===== */}
                    {(operation.signature || operation.cachet) && (
                        <Row className="g-3">
                            {operation.signature && (
                                <Col md={6}>
                                    <Card className="border-0 shadow-sm rounded-3">
                                        <Card.Header className="card-section-header">
                                            <div className="section-icon-charte" style={{ background: `${COLORS.primary}15`, color: COLORS.primary }}>
                                                <i className="ri-pen-line"></i>
                                            </div>
                                            <h6 className="mb-0 fw-semibold">Signature</h6>
                                        </Card.Header>
                                        <Card.Body className="text-center">
                                            <div className="signature-preview-charte">
                                                <img src={operation.signature} alt="Signature" />
                                            </div>
                                        </Card.Body>
                                    </Card>
                                </Col>
                            )}
                            {operation.cachet && (
                                <Col md={6}>
                                    <Card className="border-0 shadow-sm rounded-3">
                                        <Card.Header className="card-section-header">
                                            <div className="section-icon-charte" style={{ background: `${COLORS.warning}15`, color: COLORS.warning }}>
                                                <i className="ri-stamp-line"></i>
                                            </div>
                                            <h6 className="mb-0 fw-semibold">Cachet</h6>
                                        </Card.Header>
                                        <Card.Body className="text-center">
                                            <div className="signature-preview-charte">
                                                <img src={operation.cachet} alt="Cachet" />
                                            </div>
                                        </Card.Body>
                                    </Card>
                                </Col>
                            )}
                        </Row>
                    )}

                    <div className="text-center text-muted small mt-4" style={{ fontSize: '0.7rem' }}>
                        <i className="ri-information-line me-1"></i>
                        ID: #{operation.id} • Créé le {formatDate(operation.created_at)} à {new Date(operation.created_at).toLocaleTimeString('fr-FR')}
                        {operation.createur_nom && ` • Créé par: ${operation.createur_nom}`}
                    </div>
                </div>
                <Footer />
            </div>

            {/* ===== MODAL SUPPRESSION ===== */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered className="modal-charte">
                <Modal.Header closeButton className="modal-header-charte" style={{ background: `linear-gradient(135deg, ${COLORS.danger}, #dc2626)` }}>
                    <div className="d-flex align-items-center gap-3">
                        <div className="modal-header-icon"><i className="ri-delete-bin-line"></i></div>
                        <div>
                            <Modal.Title className="fs-5 fw-bold text-white mb-0">Confirmer la suppression</Modal.Title>
                            <p className="text-white-50 mb-0 small">Cette action est irréversible</p>
                        </div>
                    </div>
                </Modal.Header>
                <Modal.Body className="p-4">
                    <p className="text-center text-muted mb-3">
                        Êtes-vous sûr de vouloir supprimer définitivement ce décaissement ?
                    </p>
                    <div className="summary-card-charte mb-3">
                        <div className="d-flex align-items-center gap-3">
                            <div className="beneficiaire-avatar-charte">
                                <i className="ri-bank-card-line"></i>
                            </div>
                            <div>
                                <div className="fw-bold" style={{ color: COLORS.primaryDark }}>{operation.numero_bordereau}</div>
                                <div className="text-muted small">{operation.beneficiaire_prenom} {operation.beneficiaire_nom}</div>
                                <div className="text-muted small">
                                    <i className="ri-money-euro-circle-line me-1"></i>
                                    Montant: {formatCurrency(montantTotal)}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="alert-error-charte mb-0">
                        <div className="d-flex align-items-center">
                            <div className="alert-error-icon"><i className="ri-alert-line"></i></div>
                            <div className="ms-3">
                                <strong>Attention</strong>
                                <p className="mb-0 small">Toutes les données associées seront définitivement supprimées</p>
                            </div>
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer className="border-0 pb-4 px-4 pt-0">
                    <Button onClick={() => setShowDeleteModal(false)} className="btn-modern-outline" disabled={deleting}>
                        Annuler
                    </Button>
                    <Button onClick={handleDelete} disabled={deleting} className="btn-modern-danger">
                        {deleting ? (
                            <><Spinner size="sm" className="me-2" />Suppression...</>
                        ) : (
                            <><i className="ri-delete-bin-line me-2"></i>Oui, supprimer</>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* ===== STYLES CHARTE ===== */}
            <style>{`
                :root {
                    --primary: ${COLORS.primary || '#295AA7'};
                    --primary-dark: ${COLORS.primaryDark || '#1e3f7a'};
                    --secondary: ${COLORS.secondary || '#21ACCD'};
                    --success: ${COLORS.success || '#10b981'};
                    --danger: ${COLORS.danger || '#ef4444'};
                    --warning: ${COLORS.warning || '#f59e0b'};
                    --info: ${COLORS.info || '#3b82f6'};
                }

                /* ===== EN-TÊTE ===== */
                .header-modern {
                    background: white; padding: 1.2rem 1.8rem;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    border-left: 4px solid var(--secondary);
                    border-radius: 12px;
                    display: flex; align-items: center; justify-content: space-between;
                    flex-wrap: wrap; gap: 1rem;
                }
                .header-left { display: flex; align-items: center; gap: 1rem; }
                .header-icon-wrapper {
                    width: 52px; height: 52px;
                    background: linear-gradient(135deg, var(--primary), var(--secondary));
                    display: flex; align-items: center; justify-content: center;
                    border-radius: 14px; color: white; font-size: 1.5rem;
                    box-shadow: 0 4px 12px rgba(41, 90, 167, 0.25);
                }
                .header-title { color: #1e293b; font-size: 1.3rem; font-weight: 700; margin: 0; }
                .badge-modern {
                    background: #f1f4f9; color: #475569;
                    padding: 0.3rem 0.8rem; border-radius: 50px;
                    font-size: 0.75rem; font-weight: 600;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .badge-modern i { color: var(--secondary); }
                .status-badge-charte {
                    color: white; padding: 0.4rem 1.1rem; border-radius: 50px;
                    font-size: 0.75rem; font-weight: 600;
                    display: inline-flex; align-items: center; gap: 0.4rem;
                }

                /* ===== STAT ===== */
                .stat-item-charte { display: flex; align-items: center; gap: 0.75rem; }
                .stat-icon-charte {
                    width: 48px; height: 48px; border-radius: 12px;
                    display: flex; align-items: center; justify-content: center;
                    font-size: 1.4rem; flex-shrink: 0;
                }
                .stat-label-charte {
                    display: block; font-size: 0.7rem; font-weight: 500;
                    text-transform: uppercase; letter-spacing: 0.5px; color: #94a3b8;
                }
                .stat-value-charte { font-size: 1.05rem; font-weight: 600; color: #1e293b; }
                .kpi-unit-charte { font-size: 0.7rem; color: #94a3b8; }

                /* ===== STAT CARD ===== */
                .stat-card-charte {
                    background: white; border-radius: 12px;
                    padding: 1.25rem;
                    text-align: center;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    border-left: 4px solid;
                    transition: all 0.25s ease;
                    height: 100%;
                }
                .stat-card-charte:hover {
                    transform: translateY(-3px);
                    box-shadow: 0 12px 28px rgba(0,0,0,0.08);
                }
                .stat-card-charte.kpi-primary { border-left-color: var(--primary); }
                .stat-card-charte.kpi-success { border-left-color: var(--success); }

                /* ===== SECTIONS ===== */
                .card-section-header {
                    background: transparent; border: none;
                    padding: 1.25rem 1.5rem 0.75rem;
                    display: flex; align-items: center; gap: 0.75rem;
                }
                .section-icon-charte {
                    width: 36px; height: 36px; border-radius: 10px;
                    display: flex; align-items: center; justify-content: center;
                    font-size: 1.1rem;
                }
                .card-hover-charte { transition: all 0.3s ease; }
                .card-hover-charte:hover {
                    transform: translateY(-3px);
                    box-shadow: 0 12px 32px rgba(0,0,0,0.08) !important;
                }

                /* ===== INFO LINE ===== */
                .info-line-charte {
                    display: flex; align-items: center; gap: 0.6rem;
                    font-size: 0.85rem; color: #475569; margin-bottom: 0.6rem;
                }
                .info-line-charte i { color: var(--secondary); font-size: 1rem; }

                /* ===== PROGRESS ===== */
                .progress-charte-large {
                    height: 14px;
                    background: #e2e8f0;
                    border-radius: 50px;
                    overflow: hidden;
                    box-shadow: inset 0 2px 4px rgba(0,0,0,0.05);
                }
                .progress-bar-charte-large {
                    height: 100%;
                    background: linear-gradient(90deg, var(--success), #059669);
                    border-radius: 50px;
                    display: flex; align-items: center; justify-content: center;
                    color: white; font-weight: 700; font-size: 0.7rem;
                    transition: width 0.6s ease;
                    box-shadow: 0 2px 8px rgba(16,185,129,0.3);
                }

                /* ===== HEADER GRADIENT ===== */
                .card-header-gradient {
                    background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
                    border-top-left-radius: 12px !important;
                    border-top-right-radius: 12px !important;
                    padding: 1rem 1.5rem;
                    border: none;
                }
                .badge-header-light {
                    background: rgba(255,255,255,0.2);
                    color: white;
                    border: 1px solid rgba(255,255,255,0.3);
                    border-radius: 50px;
                    padding: 0.25rem 0.75rem;
                    font-size: 0.75rem;
                    font-weight: 600;
                }

                /* ===== TABLE ===== */
                .table-charte thead th {
                    background: #f8fafc; color: #475569;
                    font-weight: 600; font-size: 0.7rem;
                    text-transform: uppercase; letter-spacing: 0.5px;
                    border-bottom: 2px solid #e2e8f0;
                    padding: 0.9rem 0.75rem;
                }
                .table-charte tbody td {
                    padding: 0.9rem 0.75rem;
                    vertical-align: middle;
                    border-bottom: 1px solid #f1f5f9;
                    font-size: 0.85rem;
                }
                .table-charte tbody tr { transition: background 0.15s ease; }
                .table-charte tbody tr:hover { background: #f8fafc; }
                .table-charte tbody tr.row-initial-charte { background: ${COLORS.primary}08; }
                .table-charte tbody tr.row-initial-charte:hover { background: ${COLORS.primary}12; }
                .table-charte tfoot td {
                    background: #f8fafc;
                    padding: 0.85rem 0.75rem;
                    border-top: 2px solid #e2e8f0;
                    font-size: 0.85rem;
                }

                .reference-badge-charte {
                    display: inline-flex; align-items: center; gap: 4px;
                    background: #f1f5f9; padding: 0.25rem 0.6rem;
                    border-radius: 6px; font-size: 0.7rem;
                    font-family: monospace; color: #475569;
                }
                .badge-type-charte {
                    display: inline-flex; align-items: center; gap: 4px;
                    color: white; padding: 0.25rem 0.75rem;
                    border-radius: 50px; font-size: 0.7rem; font-weight: 600;
                    white-space: nowrap;
                }
                .badge-solde-charte {
                    display: inline-flex; align-items: center; gap: 3px;
                    background: ${COLORS.success}15; color: var(--success);
                    padding: 0.2rem 0.6rem; border-radius: 50px;
                    font-size: 0.7rem; font-weight: 700;
                }
                .badge-reste-charte {
                    display: inline-flex; align-items: center;
                    background: ${COLORS.danger}15; color: var(--danger);
                    padding: 0.2rem 0.6rem; border-radius: 50px;
                    font-size: 0.7rem; font-weight: 700;
                }

                /* ===== SIGNATURE ===== */
                .signature-preview-charte img {
                    max-width: 100%;
                    max-height: 150px;
                    border: 2px solid #e2e8f0;
                    border-radius: 12px;
                    padding: 10px;
                    background: white;
                }

                /* ===== ACTION BTN ===== */
                .action-btn-charte {
                    width: 42px; height: 42px; border-radius: 12px;
                    display: inline-flex; align-items: center; justify-content: center;
                    background: white; border: 1.5px solid #e2e8f0;
                    color: #64748b; font-size: 1.1rem;
                    transition: all 0.2s ease;
                    cursor: pointer;
                }
                .action-btn-charte:hover {
                    border-color: var(--btn-color); color: var(--btn-color);
                    transform: translateY(-2px);
                    box-shadow: 0 6px 16px rgba(41, 90, 167, 0.15);
                }

                /* ===== MODAL ===== */
                .modal-charte .modal-content {
                    border: none; border-radius: 20px; overflow: hidden;
                    box-shadow: 0 30px 80px rgba(0,0,0,0.15);
                }
                .modal-header-charte { padding: 1.25rem 1.5rem; border: none; }
                .modal-header-charte .btn-close { filter: brightness(0) invert(1); opacity: 0.8; }
                .modal-header-icon {
                    width: 48px; height: 48px; border-radius: 12px;
                    background: rgba(255,255,255,0.2);
                    display: flex; align-items: center; justify-content: center;
                    font-size: 1.5rem; color: white;
                }
                .summary-card-charte {
                    background: linear-gradient(135deg, #f8fafc, #ffffff);
                    padding: 1rem 1.25rem;
                    border-radius: 12px;
                    border: 1px solid #e2e8f0;
                }
                .beneficiaire-avatar-charte {
                    width: 52px; height: 52px; border-radius: 12px;
                    background: linear-gradient(135deg, var(--primary), var(--primary-dark));
                    display: flex; align-items: center; justify-content: center;
                    color: white; font-size: 1.4rem;
                    flex-shrink: 0;
                    box-shadow: 0 4px 12px rgba(41,90,167,0.25);
                }

                /* ===== BOUTONS ===== */
                .btn-modern-outline {
                    border: 1.5px solid #d1d5db; background: white;
                    padding: 0.5rem 1.4rem; border-radius: 50px;
                    font-size: 0.85rem; font-weight: 500; color: #4b5563;
                    transition: all 0.2s ease;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-outline:hover {
                    border-color: var(--secondary); color: var(--secondary);
                    background: #f0fbff; transform: translateY(-1px);
                    box-shadow: 0 4px 8px rgba(33,172,205,0.15);
                }
                .btn-modern-primary {
                    background: linear-gradient(135deg, var(--primary), var(--primary-dark));
                    border: none; color: white; padding: 0.5rem 1.6rem;
                    border-radius: 50px; font-weight: 600; font-size: 0.85rem;
                    transition: all 0.2s ease;
                    box-shadow: 0 4px 12px rgba(41,90,167,0.3);
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-primary:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 18px rgba(41,90,167,0.4); color: white;
                }
                .btn-modern-danger {
                    background: linear-gradient(135deg, var(--danger), #dc2626);
                    border: none; color: white; padding: 0.5rem 1.6rem;
                    border-radius: 50px; font-weight: 600; font-size: 0.85rem;
                    transition: all 0.2s ease;
                    box-shadow: 0 4px 12px rgba(239,68,68,0.3);
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-danger:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 18px rgba(239,68,68,0.4); color: white;
                }
                .btn-modern-primary:disabled,
                .btn-modern-danger:disabled { opacity: 0.6; transform: none; }

                /* ===== ALERTES ===== */
                .alert-error-charte {
                    border: none; background: #fef2f2;
                    border-left: 4px solid var(--danger);
                    border-radius: 16px; padding: 1rem 1.5rem;
                    box-shadow: 0 8px 32px rgba(239,68,68,0.08);
                }
                .alert-error-icon {
                    width: 48px; height: 48px; border-radius: 12px;
                    background: rgba(239,68,68,0.1);
                    display: flex; align-items: center; justify-content: center;
                    color: var(--danger); font-size: 1.5rem;
                }

                /* ===== LOADING ===== */
                .loading-spinner-charte { position: relative; width: 80px; height: 80px; margin: 0 auto; }
                .loading-ring-charte {
                    position: absolute; inset: 0; border-radius: 50%;
                    border: 4px solid #e2e8f0; border-top-color: var(--primary);
                    animation: spin 1s linear infinite;
                }
                .loading-icon-charte {
                    position: absolute; top: 50%; left: 50%;
                    transform: translate(-50%, -50%);
                    font-size: 28px; color: var(--primary);
                }

                /* ===== TOAST ===== */
                .toast-notification-fixed {
                    position: fixed; bottom: 24px; right: 24px;
                    z-index: 9999; animation: slideInRight 0.3s ease-out;
                }
                .toast-modern {
                    background: white; border-radius: 16px;
                    box-shadow: 0 20px 40px rgba(0,0,0,0.15);
                    min-width: 320px; max-width: 400px; overflow: hidden;
                }
                .toast-header-custom {
                    padding: 12px 20px;
                    display: flex; align-items: center; justify-content: space-between;
                }
                .toast-close-btn {
                    background: transparent; border: none;
                    color: white; cursor: pointer; opacity: 0.8;
                }
                .toast-close-btn:hover { opacity: 1; }
                .toast-body-custom { padding: 16px 20px; }
                .toast-body-custom p { color: #1e293b; font-size: 0.9rem; }
                .toast-progress { height: 3px; width: 100%; animation: shrink linear forwards; }

                /* ===== ANIMATIONS ===== */
                @keyframes spin { to { transform: rotate(360deg); } }
                @keyframes slideInRight {
                    from { opacity: 0; transform: translateX(100%); }
                    to { opacity: 1; transform: translateX(0); }
                }
                @keyframes shrink {
                    from { width: 100%; }
                    to { width: 0%; }
                }

                /* ===== RESPONSIVE ===== */
                @media (max-width: 768px) {
                    .header-modern { padding: 1rem; }
                    .header-icon-wrapper { width: 44px; height: 44px; font-size: 1.2rem; }
                    .header-title { font-size: 1.1rem; }
                    .toast-notification-fixed { bottom: 12px; right: 12px; left: 12px; }
                    .toast-modern { min-width: auto; max-width: none; }
                }
                .rounded-3 { border-radius: 12px !important; }
            `}</style>
        </>
    );
}